import { useSelector } from 'react-redux';

import { DeliveryProviderService } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProvider';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import type {
  AccountData,
  ProviderPortalAssignedProvider,
} from '@provider-portal/common/services/types/account';
import {
  AccountStage,
  getSelectedProviderData,
} from '@provider-portal/common/services/types/account';

/**
 * Duplicate interfaces: circular dependencies on import
 * @see file://./../../../app/src/rdx/account/reducer.ts
 */
interface GetProviderPortalAccountInfo {
  account_data: AccountData;
  assignments: ProviderPortalAssignedProvider[];
}

interface GetProviderPortalAccountInfoResponse {
  account_info: GetProviderPortalAccountInfo;
}

interface AccountState {
  details?: GetProviderPortalAccountInfoResponse;
  selectedProvider?: number;
}

interface FauxGlobalAppState {
  account: AccountState;
}

/**
 * Duplicate selectors: circular dependencies on import
 * @see file://./../../../app/src/rdx/account/selectors.ts
 */
function selectAccountOnboarding(state: FauxGlobalAppState): boolean {
  return state.account.details?.account_info.account_data.is_account_onboarding ?? false;
}

function selectAssignedProviders(state: FauxGlobalAppState): ProviderPortalAssignedProvider[] {
  return state.account.details?.account_info.assignments ?? [];
}

function getAccountStage(
  isOnboarding: boolean,
  assignedProviders: ProviderPortalAssignedProvider[],
): AccountStage {
  if (isOnboarding) {
    if (assignedProviders.length < 2) {
      return AccountStage.ONBOARDING;
    }
  }

  return AccountStage.ACTIVATED;
}

export function selectSignupSource(
  state: FauxGlobalAppState,
): DeliveryProviderService.ProviderSignupSource {
  const selectedProviderId = state.account.selectedProvider ?? 0;
  const selectedProvider = getSelectedProviderData(
    selectedProviderId,
    selectAssignedProviders(state),
  );
  return selectedProvider.signup_source;
}

export function selectAssignedProvidersCount(state: FauxGlobalAppState): number {
  return selectAssignedProviders(state).length;
}

export function useAccountStage(): AccountStage {
  const isOnboarding = useSelector(selectAccountOnboarding);
  const assignedProviders = useSelector(selectAssignedProviders);

  return getAccountStage(isOnboarding, assignedProviders);
}

export function useAccountStageObservable(): AccountStage {
  const commonAppServices = useCommonAppServices();
  const isOnboarding = commonAppServices.accountService.createAccountDetailsSelector$(
    (accountDetails) =>
      selectAccountOnboarding({
        account: {
          details: {
            account_info: accountDetails,
          },
        },
      }),
  ).state;
  const assignedProviders = commonAppServices.accountService.createAccountDetailsSelector$(
    (accountDetails) =>
      selectAssignedProviders({
        account: {
          details: {
            account_info: accountDetails,
          },
        },
      }),
  ).state;

  return getAccountStage(isOnboarding, assignedProviders);
}

export function useIsAccountOnboarding(): boolean {
  return useAccountStage() === AccountStage.ONBOARDING;
}

export function useIsAccountOnboardingObservable(): boolean {
  return useAccountStageObservable() === AccountStage.ONBOARDING;
}
